import React, { useEffect, useState } from "react";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { Grid } from "@mui/material";
import Logger from "../../../services/Logger";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomFabs from "../../components/CustomFabs";
import CustomSnackbar from "../../components/CustomSnackbar";
import KeywordBox from "../../components/KeywordBox";
import CreatorGridService from "../../../creator/service/CreatorGridService";
import {
    adminTagFilterAutocompleteSlotProps,
    adminTagFilterChipSx,
    filterAdminTagOptions,
} from "../../pzl_common/board/adminTagFilterChipSx";
import {
    PUBLISHED_LIST_ORDER_OPTIONS,
    publishedListSort,
} from "../../pzl_common/board/publishedListOrder";
import CustomStateChip from "../../components/CustomStateChip";
import { resolvePreviewImageUrl } from "../../pzl_common/board/PreviewImageDialog";
import ApiService from "../../../services/ApiService";
import AdminGridService from "./AdminGridService";
import AdminGridBoardService from "../board/AdminGridBoardService";
import { gridBoardDetailPath } from "../board/gridBoardDetailPath";
import { getColumns } from "./getColumns";

// State constants
const ALL = "ALL";
const DRAFT = "DRAFT";

const READY_TO_REVIEW = "READY_TO_REVIEW";

const REVIEWED = "REVIEWED";
const PUBLISHED = "PUBLISHED";
const DISCARDED = "DISCARDED";
const DISABLED = "DISABLED";

const STORAGE_KEY = "admin.grid_tpl.filters";
const GRID_TPL_TAGS_KEY = "admin.grid_tpl.tags";
const VALID_STATES = [ALL, DRAFT, READY_TO_REVIEW, REVIEWED, PUBLISHED, DISCARDED, DISABLED];
const GRID_TPL_ORDER_DEFAULT = "createdAt:desc";
const VALID_ORDER_VALUES = new Set(PUBLISHED_LIST_ORDER_OPTIONS.map((o) => o.value));
const VALID_VIEW_MODES = ["list", "grid"];
const ACCESS_ALL = "all";
const ACCESS_FREE = "free";
const ACCESS_PAID = "paid";
const VALID_ACCESS = [ACCESS_ALL, ACCESS_FREE, ACCESS_PAID];
const TYPE_ALL = "all";
const TYPE_WORD_SEARCH = "WORD_SEARCH";
const TYPE_HIDDEN_MESSAGE = "HIDDEN_MESSAGE";
const VALID_TYPES = [TYPE_ALL, TYPE_WORD_SEARCH, TYPE_HIDDEN_MESSAGE];

function gridTplSort(orderValue) {
    if (VALID_ORDER_VALUES.has(orderValue)) {
        return publishedListSort(orderValue);
    }
    return [{ field: "createdAt", sort: "desc" }];
}

function orderValueFromSort(sortModel) {
    const field = sortModel?.[0]?.field;
    const dir = sortModel?.[0]?.sort;
    const value = `${field}:${dir}`;
    return VALID_ORDER_VALUES.has(value) ? value : GRID_TPL_ORDER_DEFAULT;
}

function sameSort(left, right) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
        return false;
    }
    return left.every((item, index) => item?.field === right[index]?.field && item?.sort === right[index]?.sort);
}

const loadStoredFilters = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                language: ["all", "english", "french"].includes(parsed?.language) ? parsed.language : "all",
                type: VALID_TYPES.includes(parsed?.type) ? parsed.type : TYPE_ALL,
                state: VALID_STATES.includes(parsed?.state) ? parsed.state : ALL,
                creator: typeof parsed?.creator === "string" ? parsed.creator : "",
                keyword: typeof parsed?.keyword === "string" ? parsed.keyword : "",
                showDisabled: !!parsed?.showDisabled,
                order: VALID_ORDER_VALUES.has(parsed?.order) ? parsed.order : GRID_TPL_ORDER_DEFAULT,
                viewMode: VALID_VIEW_MODES.includes(parsed?.viewMode) ? parsed.viewMode : "list",
                access: VALID_ACCESS.includes(parsed?.access) ? parsed.access : ACCESS_ALL,
            };
        }
    } catch (e) {
        Logger.error("Error reading grid_tpl filters from localStorage:", e);
    }
    return { language: "all", type: TYPE_ALL, state: ALL, creator: "", keyword: "", showDisabled: false, order: GRID_TPL_ORDER_DEFAULT, viewMode: "list", access: ACCESS_ALL };
};

const saveStoredFilters = (filters) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
        Logger.error("Error saving grid_tpl filters to localStorage:", e);
    }
};

const loadStoredTagsFilter = () => {
    try {
        const stored = localStorage.getItem(GRID_TPL_TAGS_KEY);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((t) => String(t || "").trim())
            .filter((t) => t.length > 0);
    } catch (e) {
        Logger.error("Error reading grid_tpl tags from localStorage:", e);
    }
    return [];
};

const saveStoredTagsFilter = (tags) => {
    try {
        if (tags && Array.isArray(tags) && tags.length > 0) {
            localStorage.setItem(GRID_TPL_TAGS_KEY, JSON.stringify(tags));
        } else {
            localStorage.removeItem(GRID_TPL_TAGS_KEY);
        }
    } catch (e) {
        Logger.error("Error saving grid_tpl tags to localStorage:", e);
    }
};

function tagOptionValue(option) {
    if (typeof option === "string") return option;
    if (option != null && typeof option === "object") {
        return String(option.tag ?? option.name ?? "");
    }
    return "";
}

function tagOptionLabel(option) {
    const name = tagOptionValue(option);
    const count = typeof option === "object" && option != null ? option.count : undefined;
    if (count != null && count !== "") return `${name} (${count})`;
    return name;
}

const defaultRePublishSince = () => dayjs().subtract(6, "month").startOf("day");

export default function AdminGridTplMain() {
    const navigate = useNavigate();
    const initialFilters = loadStoredFilters();

    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 15 });
    const [sort, setSort] = useState(() => gridTplSort(initialFilters.order));
    const [viewMode, setViewMode] = useState(initialFilters.viewMode);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [keyword, setKeyword] = useState(initialFilters.keyword);
    const [selectedLanguage, setSelectedLanguage] = useState(initialFilters.language);
    const [selectedType, setSelectedType] = useState(initialFilters.type);
    const [selectedState, setSelectedState] = useState(initialFilters.state);
    const [selectedAccess, setSelectedAccess] = useState(initialFilters.access);
    const [selectedGridSize, setSelectedGridSize] = useState("all"); // "all" | "classic" | "mini"
    const [creators, setCreators] = useState([]);
    const [selectedCreator, setSelectedCreator] = useState(initialFilters.creator);
    const [selectedTags, setSelectedTags] = useState(loadStoredTagsFilter);
    const [availableTags, setAvailableTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [publishingId, setPublishingId] = useState(null);
    const [savingFreeId, setSavingFreeId] = useState(null);
    const [regeneratingId, setRegeneratingId] = useState(null);
    const [showDisabledColumn, setShowDisabledColumn] = useState(initialFilters.showDisabled);
    const [reload, setReload] = useState();

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState(false);
    const [message, setMessage] = useState("");
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [openGenWordNetworkDialog, setOpenGenWordNetworkDialog] = useState(false);
    const [genWordNetworkLoading, setGenWordNetworkLoading] = useState(false);
    const [genWordNetworkResult, setGenWordNetworkResult] = useState(null);
    const [openGenPreImageDialog, setOpenGenPreImageDialog] = useState(false);
    const [genPreImageLoading, setGenPreImageLoading] = useState(false);
    const [genPreImageResult, setGenPreImageResult] = useState(null);
    const [genPreImageForce, setGenPreImageForce] = useState(false);
    const [openGenPdfDialog, setOpenGenPdfDialog] = useState(false);
    const [genPdfLoading, setGenPdfLoading] = useState(false);
    const [genPdfResult, setGenPdfResult] = useState(null);
    const [genPdfMode, setGenPdfMode] = useState("missing");
    const [openGenBubblesDialog, setOpenGenBubblesDialog] = useState(false);
    const [genBubblesLoading, setGenBubblesLoading] = useState(false);
    const [genBubblesResult, setGenBubblesResult] = useState(null);
    const [genBubblesPage, setGenBubblesPage] = useState(0);
    const [genBubblesBatchSize, setGenBubblesBatchSize] = useState(300);
    const [genBubblesForce, setGenBubblesForce] = useState(false);
    const [openDuplicateScatteredDialog, setOpenDuplicateScatteredDialog] = useState(false);
    const [duplicateScatteredLoading, setDuplicateScatteredLoading] = useState(false);
    const [duplicateScatteredResult, setDuplicateScatteredResult] = useState(null);
    const [openRePublishDialog, setOpenRePublishDialog] = useState(false);
    const [rePublishLoading, setRePublishLoading] = useState(false);
    const [rePublishResult, setRePublishResult] = useState(null);
    const [rePublishSince, setRePublishSince] = useState(() => defaultRePublishSince());
    const [rePublishDateError, setRePublishDateError] = useState("");

    useEffect(() => {
        AdminGridService.requestCreators()
            .then(setCreators)
            .catch((err) => Logger.error("Error loading creators:", err));
    }, []);

    useEffect(() => {
        let cancelled = false;
        const fetchTags = async () => {
            setTagsLoading(true);
            try {
                const language = selectedLanguage === "all" ? "english" : selectedLanguage;
                const state = selectedState === ALL ? undefined : selectedState;
                const tags = await CreatorGridService.getGridTags(language, state);
                if (cancelled) return;
                const list = Array.isArray(tags) ? tags : [];
                const sorted = [...list].sort((a, b) =>
                    tagOptionValue(a).localeCompare(tagOptionValue(b), undefined, { sensitivity: "base" }),
                );
                setAvailableTags(sorted);
            } catch (error) {
                Logger.error("Error fetching grid_tpl tags:", error);
                if (!cancelled) setAvailableTags([]);
            } finally {
                if (!cancelled) setTagsLoading(false);
            }
        };
        fetchTags();
        return () => {
            cancelled = true;
        };
    }, [selectedLanguage, selectedState]);

    const sortKey = sort.map((item) => `${item.field}:${item.sort}`).join(",");
    const tagsKey = Array.isArray(selectedTags) ? selectedTags.join("\u0001") : "";

    useEffect(() => {
        AdminGridService.requestList(
            pageModel,
            sort,
            setRows,
            setRowCount,
            keyword,
            selectedState === ALL ? null : selectedState,
            selectedLanguage,
            selectedType === TYPE_ALL ? null : selectedType,
            selectedCreator || null,
            selectedTags,
            selectedAccess === ACCESS_FREE ? true : selectedAccess === ACCESS_PAID ? false : null,
            selectedGridSize === "all" ? null : selectedGridSize,
        );
        // sort and selectedTags are read through sortKey and tagsKey so a new array with the same values does not refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyword, pageModel.page, pageModel.pageSize, sortKey, reload, selectedState, selectedLanguage, selectedType, selectedCreator, tagsKey, selectedAccess, selectedGridSize]);

    const handleTagsChange = (newTags) => {
        setSelectedTags(newTags);
        saveStoredTagsFilter(newTags);
        setPageModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleCell = (cell) => {
        // Don't navigate if clicking on checkbox column
        if (cell.field === "__check__" || cell.field === "actions" || cell.field === "free") {
            return;
        }
        const row = rows.find((r) => r.id === cell.id) || { id: cell.id, state: cell.row?.state };
        navigate(gridBoardDetailPath(row, 0));
    };

    const persistFilters = (overrides = {}) => {
        saveStoredFilters({
            language: selectedLanguage,
            type: selectedType,
            state: selectedState,
            creator: selectedCreator,
            keyword,
            showDisabled: showDisabledColumn,
            order: orderValueFromSort(sort),
            viewMode,
            access: selectedAccess,
            ...overrides,
        });
    };

    const handleSort = (model) => {
        const next = model?.length ? model : gridTplSort(GRID_TPL_ORDER_DEFAULT);
        if (sameSort(sort, next)) {
            return;
        }
        setSort(next);
        persistFilters({ order: orderValueFromSort(next) });
    };

    const handlePaginationModelChange = (model) => {
        if (!model) return;
        setPageModel((prev) => {
            if (prev.page === model.page && prev.pageSize === model.pageSize) {
                return prev;
            }
            return { page: model.page, pageSize: model.pageSize };
        });
    };

    const handleOrderChange = (value) => {
        setSort(gridTplSort(value));
        setPageModel((prev) => ({ ...prev, page: 0 }));
        persistFilters({ order: value });
    };

    const handleViewModeChange = (_event, next) => {
        if (!next) return;
        setViewMode(next);
        persistFilters({ viewMode: next });
    };

    const handleSelectionModel = (newSelectionModel) => {
        setSelectedRows(newSelectionModel);
    };

    const handleDeleteClick = () => {
        if (selectedRows.length > 0) {
            setOpenConfirmDialog(true);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            // Delete all selected rows
            await AdminGridService.deleteTemplates(selectedRows);

            handleSnackBar("success", `${selectedRows.length} template(s) deleted successfully`);
            setReload(moment());

            // Clear selection after deletion
            setSelectedRows([]);
            setOpenConfirmDialog(false);
        } catch (error) {
            Logger.error("Error deleting templates:", error);
            handleSnackBar("error", "Error deleting templates");
        }
    };

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };

    const handleFreeChange = async (row, free) => {
        if (!row?.id || savingFreeId) return;
        setSavingFreeId(row.id);
        try {
            const result = await AdminGridBoardService.setFree(row.id, free);
            if (result?.code === "FAILURE") {
                throw new Error(result.message || "Failed to update free status");
            }
            const leavesAccessFilter =
                (selectedAccess === ACCESS_FREE && !free) || (selectedAccess === ACCESS_PAID && free);
            setRows((prev) => (
                leavesAccessFilter
                    ? prev.filter((item) => item.id !== row.id)
                    : prev.map((item) => (item.id === row.id ? { ...item, free } : item))
            ));
            if (leavesAccessFilter) {
                setRowCount((count) => Math.max(0, count - 1));
            }
            handleSnackBar("success", free ? "Set to free" : "Set to paid");
        } catch (err) {
            Logger.error("Error setting free:", err);
            handleSnackBar("error", err?.message || "Failed to update free status");
        } finally {
            setSavingFreeId(null);
        }
    };

    const handlePublishRow = async (row) => {
        if (!row?.id || publishingId || String(row.state || "").toUpperCase() === "PUBLISHED") return;
        setPublishingId(row.id);
        try {
            await AdminGridService.publish(row.id, row.free === true);
            handleSnackBar("success", "Published");
            setReload(moment());
        } catch (err) {
            Logger.error("Error publishing template:", err);
            handleSnackBar("error", err?.message || "Publish failed");
        } finally {
            setPublishingId(null);
        }
    };

    const handleRegenPreview = async (row) => {
        if (!row?.id || regeneratingId) return;
        setRegeneratingId(row.id);
        try {
            await AdminGridService.genPreviewImage(row.id);
            handleSnackBar("success", "Preview image regenerated");
            setReload(moment());
        } catch (err) {
            Logger.error("Error regenerating preview:", err);
            handleSnackBar("error", err?.message || "Failed to regenerate preview");
        } finally {
            setRegeneratingId(null);
        }
    };

    const handleFabClick = (index) => {
        if (index === 0) {
            navigate("/admin/puzzle/grid_tpl/create");
        }
    };

    const handleGenWordNetworkClick = () => {
        setGenWordNetworkResult(null);
        setOpenGenWordNetworkDialog(true);
    };

    const handleGenWordNetworkConfirm = async () => {
        setGenWordNetworkLoading(true);
        setGenWordNetworkResult(null);
        try {
            const result = await AdminGridService.genWordNetworkForNull();
            setGenWordNetworkResult(result);
            setReload(moment());
        } catch (err) {
            Logger.error("Error generating word network:", err);
            setGenWordNetworkResult({ error: err?.message || "Failed to generate word network" });
        } finally {
            setGenWordNetworkLoading(false);
        }
    };

    const handleGenWordNetworkClose = () => {
        setOpenGenWordNetworkDialog(false);
        setGenWordNetworkResult(null);
    };

    const handleGenPreImageClick = () => {
        setGenPreImageResult(null);
        setGenPreImageForce(false);
        setOpenGenPreImageDialog(true);
    };

    const handleGenPreImageConfirm = async () => {
        setGenPreImageLoading(true);
        setGenPreImageResult(null);
        try {
            const result = await AdminGridService.genPreImageForNull(genPreImageForce);
            setGenPreImageResult(result);
            setReload(moment());
        } catch (err) {
            Logger.error("Error generating preview images:", err);
            setGenPreImageResult({ error: err?.message || "Failed to generate preview images" });
        } finally {
            setGenPreImageLoading(false);
        }
    };

    const handleGenPreImageClose = () => {
        setOpenGenPreImageDialog(false);
        setGenPreImageResult(null);
        setGenPreImageForce(false);
    };

    const handleGenPdfClick = () => {
        setGenPdfResult(null);
        setGenPdfMode("missing");
        setOpenGenPdfDialog(true);
    };

    const handleGenPdfConfirm = async () => {
        setGenPdfLoading(true);
        setGenPdfResult(null);
        try {
            const result = genPdfMode === "all-existing"
                ? await AdminGridService.replaceAllPdfsAsync()
                : await AdminGridService.genPdfForNullAsync();
            setGenPdfResult(result);
            setReload(moment());
        } catch (err) {
            Logger.error("Error generating PDFs:", err);
            setGenPdfResult({ error: err?.message || "Failed to generate PDFs" });
        } finally {
            setGenPdfLoading(false);
        }
    };

    const handleGenPdfClose = () => {
        setOpenGenPdfDialog(false);
        setGenPdfResult(null);
    };

    const handleGenBubblesClick = () => {
        setGenBubblesResult(null);
        setGenBubblesPage(0);
        setGenBubblesBatchSize(300);
        setGenBubblesForce(false);
        setOpenGenBubblesDialog(true);
    };

    const handleGenBubblesConfirm = async () => {
        setGenBubblesLoading(true);
        setGenBubblesResult(null);
        try {
            const page = Math.max(0, Math.floor(Number(genBubblesPage) || 0));
            const batchSize = Math.max(1, Math.floor(Number(genBubblesBatchSize) || 300));
            const result = await AdminGridService.genClusteredBubbles(page, batchSize, genBubblesForce);
            setGenBubblesResult(result);
            setReload(moment());
        } catch (err) {
            Logger.error("Error generating clustered bubbles:", err);
            setGenBubblesResult({ error: err?.message || "Failed to generate clustered bubbles" });
        } finally {
            setGenBubblesLoading(false);
        }
    };

    const handleGenBubblesClose = () => {
        setOpenGenBubblesDialog(false);
        setGenBubblesResult(null);
    };

    const handleDuplicateScatteredClose = () => {
        setOpenDuplicateScatteredDialog(false);
        setDuplicateScatteredResult(null);
    };

    const handleVerifyDuplicateScattered = async () => {
        setDuplicateScatteredResult(null);
        setOpenDuplicateScatteredDialog(true);
        setDuplicateScatteredLoading(true);
        try {
            const data = await ApiService.getData("/test/grid/published-hidden-message/duplicate-scattered");
            setDuplicateScatteredResult(data);
        } catch (err) {
            Logger.error("Verify duplicate-scattered:", err);
            setDuplicateScatteredResult({ error: err?.message || "Request failed" });
        } finally {
            setDuplicateScatteredLoading(false);
        }
    };

    const handleRePublishClick = () => {
        setRePublishResult(null);
        setRePublishDateError("");
        setRePublishSince(defaultRePublishSince());
        setOpenRePublishDialog(true);
    };

    const handleRePublishConfirm = async () => {
        if (!rePublishSince || !rePublishSince.isValid()) {
            setRePublishDateError("Select a valid Since date.");
            return;
        }
        setRePublishDateError("");
        setRePublishLoading(true);
        setRePublishResult(null);
        try {
            const sinceStr = rePublishSince.format("YYYY-MM-DD");
            const result = await AdminGridService.rePublish(sinceStr);
            if (result && result.error) {
                setRePublishResult({
                    error: typeof result.error === "string" ? result.error : String(result.error),
                });
            } else {
                setRePublishResult(result);
                setReload(moment());
            }
        } catch (err) {
            Logger.error("Error re-publishing grid templates:", err);
            setRePublishResult({ error: err?.message || "Failed to re-publish" });
        } finally {
            setRePublishLoading(false);
        }
    };

    const handleRePublishClose = () => {
        setOpenRePublishDialog(false);
        setRePublishResult(null);
        setRePublishDateError("");
    };

    return (
        <Box sx={{ flexGrow: 1, height: "50%" }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                        <Paper elevation={2} sx={{ p: 1.5, width: "100%" }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleVerifyDuplicateScattered}
                                sx={{ minWidth: 160 }}
                            >
                                Verify duplicate-scattered
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate("/admin/puzzle/grid_tpl/verify-coord")}
                                sx={{ minWidth: 140 }}
                            >
                                Verify Coordinates
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleGenWordNetworkClick}
                                sx={{ minWidth: 160 }}
                            >
                                GEN WORD NETWORK
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleGenPreImageClick}
                                sx={{ minWidth: 130 }}
                            >
                                GEN PRE IMAGE
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleGenPdfClick}
                                sx={{ minWidth: 100 }}
                            >
                                GEN PDF
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleGenBubblesClick}
                                sx={{ minWidth: 120 }}
                            >
                                GEN BUBBLES
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleRePublishClick}
                                sx={{ minWidth: 120 }}
                            >
                                Re-Publish
                            </Button>
                            </Box>
                        </Paper>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Word Grid Template
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={viewMode}
                                    onChange={handleViewModeChange}
                                    aria-label="View mode"
                                    sx={{ mb: 0.5 }}
                                >
                                    <ToggleButton value="list" aria-label="List view" title="List">
                                        <ViewListIcon fontSize="small" />
                                    </ToggleButton>
                                    <ToggleButton value="grid" aria-label="Grid view" title="Grid">
                                        <ViewModuleIcon fontSize="small" />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <FormControl sx={{ minWidth: 120 }}>
                                <InputLabel id="language-select-label">Language</InputLabel>
                                <Select
                                    labelId="language-select-label"
                                    value={selectedLanguage}
                                    label="Language"
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setSelectedLanguage(v);
                                        persistFilters({ language: v });
                                    }}
                                    variant="standard"
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="english">English</MenuItem>
                                    <MenuItem value="french">French</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 160 }}>
                                <InputLabel id="type-select-label">Type</InputLabel>
                                <Select
                                    labelId="type-select-label"
                                    value={selectedType}
                                    label="Type"
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setSelectedType(v);
                                        setPageModel((prev) => ({ ...prev, page: 0 }));
                                        persistFilters({ type: v });
                                    }}
                                    variant="standard"
                                >
                                    <MenuItem value={TYPE_ALL}>All</MenuItem>
                                    <MenuItem value={TYPE_WORD_SEARCH}>Word Search</MenuItem>
                                    <MenuItem value={TYPE_HIDDEN_MESSAGE}>Hidden Message</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 150 }}>
                                <InputLabel id="state-select-label">State</InputLabel>
                                <Select
                                    labelId="state-select-label"
                                    value={selectedState}
                                    label="State"
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setSelectedState(v);
                                        persistFilters({ state: v });
                                    }}
                                    variant="standard"
                                >
                                    <MenuItem value={ALL}>All States</MenuItem>
                                    <MenuItem value={DRAFT}>Draft</MenuItem>
                                    <MenuItem value={READY_TO_REVIEW}>Ready to Review</MenuItem>
                                    <MenuItem value={REVIEWED}>Reviewed</MenuItem>
                                    <MenuItem value={PUBLISHED}>Published</MenuItem>
                                    <MenuItem value={DISCARDED}>Discarded</MenuItem>
                                    <MenuItem value={DISABLED}>Disabled</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 120 }}>
                                <InputLabel id="access-select-label">Access</InputLabel>
                                <Select
                                    labelId="access-select-label"
                                    value={selectedAccess}
                                    label="Access"
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setSelectedAccess(v);
                                        setPageModel((prev) => ({ ...prev, page: 0 }));
                                        persistFilters({ access: v });
                                    }}
                                    variant="standard"
                                >
                                    <MenuItem value={ACCESS_ALL}>All</MenuItem>
                                    <MenuItem value={ACCESS_FREE}>Free</MenuItem>
                                    <MenuItem value={ACCESS_PAID}>Paid</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 110 }}>
                                <InputLabel id="grid-size-select-label">Size</InputLabel>
                                <Select
                                    labelId="grid-size-select-label"
                                    value={selectedGridSize}
                                    label="Size"
                                    onChange={(e) => {
                                        setSelectedGridSize(e.target.value);
                                        setPageModel((prev) => ({ ...prev, page: 0 }));
                                    }}
                                    variant="standard"
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="classic">Classic 12×12</MenuItem>
                                    <MenuItem value="mini">Mini</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 140 }}>
                                <InputLabel id="creator-select-label">Creator</InputLabel>
                                <Select
                                    labelId="creator-select-label"
                                    value={selectedCreator}
                                    label="Creator"
                                    onChange={(e) => setSelectedCreator(e.target.value)}
                                    variant="standard"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {creators.map((c) => {
                                        const id = c.id ?? c.cloudUserId ?? "";
                                        return (
                                            <MenuItem key={id} value={id}>
                                                {[c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || id}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            <Autocomplete
                                multiple
                                freeSolo
                                size="small"
                                disableCloseOnSelect
                                options={availableTags}
                                value={selectedTags.map(
                                    (s) =>
                                        availableTags.find(
                                            (o) => tagOptionValue(o).toLowerCase() === s.toLowerCase(),
                                        ) ?? s,
                                )}
                                isOptionEqualToValue={(opt, val) =>
                                    tagOptionValue(opt).toLowerCase() === tagOptionValue(val).toLowerCase()
                                }
                                getOptionKey={(option) => tagOptionValue(option).toLowerCase()}
                                filterOptions={(options, params) =>
                                    filterAdminTagOptions(options, params, tagOptionValue, selectedTags)
                                }
                                onChange={(_event, newValue) => {
                                    handleTagsChange(
                                        newValue
                                            .map((tag) => tagOptionValue(tag).trim())
                                            .filter((tag) => tag.length > 0),
                                    );
                                }}
                                loading={tagsLoading}
                                noOptionsText={tagsLoading ? "Loading tags…" : "Type a tag and press Enter"}
                                slotProps={adminTagFilterAutocompleteSlotProps}
                                sx={{
                                    minWidth: 200,
                                    maxWidth: 280,
                                    width: { xs: "100%", sm: 240 },
                                }}
                                getOptionLabel={tagOptionLabel}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            variant="outlined"
                                            label={tagOptionValue(option)}
                                            {...getTagProps({ index })}
                                            sx={adminTagFilterChipSx}
                                            key={`${tagOptionValue(option)}-${index}`}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        label="Tags"
                                        placeholder="Filter by tags"
                                    />
                                )}
                            />
                            <KeywordBox
                                initialValue={initialFilters.keyword}
                                callback={(k) => {
                                    setKeyword(k);
                                    persistFilters({ keyword: k });
                                }}
                            />
                            <FormControl sx={{ minWidth: 180 }}>
                                <InputLabel id="grid-tpl-order-label">Order</InputLabel>
                                <Select
                                    labelId="grid-tpl-order-label"
                                    value={orderValueFromSort(sort)}
                                    label="Order"
                                    onChange={(e) => handleOrderChange(e.target.value)}
                                    variant="standard"
                                >
                                    {PUBLISHED_LIST_ORDER_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showDisabledColumn}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setShowDisabledColumn(v);
                                            persistFilters({ showDisabled: v });
                                        }}
                                        size="small"
                                    />
                                }
                                label="Show disabled"
                            />
                            </Box>
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleDeleteClick}
                                disabled={selectedRows.length === 0}
                                sx={{ minWidth: 100 }}
                            >
                                Delete ({selectedRows.length})
                            </Button>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    {viewMode === "grid" ? (
                        <Box>
                            {rows.length === 0 ? (
                                <Paper sx={{ py: 4, textAlign: "center" }}>
                                    <Typography color="text.secondary">No templates found.</Typography>
                                </Paper>
                            ) : (
                                <Grid container spacing={1.5}>
                                    {rows.map((row) => {
                                        const imageUrl = resolvePreviewImageUrl(row);
                                        const title = row.title || row.name || "Untitled";
                                        return (
                                            <Grid key={row.id} size={{ xs: 6, sm: 4, md: 3 }}>
                                                <Box
                                                    onClick={() => navigate(gridBoardDetailPath(row, 0))}
                                                    sx={{ cursor: "pointer" }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        title={title}
                                                        sx={{ mb: 0.5, fontWeight: 600, lineHeight: 1.3 }}
                                                    >
                                                        {title}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            position: "relative",
                                                            width: "100%",
                                                            aspectRatio: "1200 / 630",
                                                            borderRadius: 1,
                                                            overflow: "hidden",
                                                            border: 1,
                                                            borderColor: "divider",
                                                            bgcolor: "grey.100",
                                                        }}
                                                    >
                                                        {imageUrl ? (
                                                            <Box
                                                                component="img"
                                                                src={imageUrl}
                                                                alt={title}
                                                                sx={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "cover",
                                                                    display: "block",
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                }}
                                                            >
                                                                <Typography variant="caption" color="text.secondary">
                                                                    No image
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    <Box
                                                        sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 0.25 }}
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        <CustomStateChip label={row.state} size="small" />
                                                        <FormControlLabel
                                                            sx={{ m: 0, ml: 0.5 }}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={row.free === true}
                                                                    disabled={!row.id || savingFreeId === row.id}
                                                                    onChange={(event) => handleFreeChange(row, event.target.checked)}
                                                                    slotProps={{
                                                                        input: {
                                                                            "aria-label": row.free === true
                                                                                ? "Free, click to set paid"
                                                                                : "Paid, click to set free",
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={<Typography variant="caption">Free</Typography>}
                                                        />
                                                        <Tooltip title={regeneratingId === row.id ? "Regenerating preview…" : "Regenerate preview"}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="Regenerate preview"
                                                                    disabled={!row.id || Boolean(regeneratingId)}
                                                                    onClick={() => handleRegenPreview(row)}
                                                                >
                                                                    <ImageOutlinedIcon
                                                                        fontSize="small"
                                                                        color={regeneratingId === row.id ? "disabled" : "action"}
                                                                    />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title={String(row.state || "").toUpperCase() === "PUBLISHED" ? "Published" : "Publish"}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="Publish"
                                                                    disabled={
                                                                        String(row.state || "").toUpperCase() === "PUBLISHED"
                                                                        || Boolean(publishingId)
                                                                    }
                                                                    onClick={() => handlePublishRow(row)}
                                                                >
                                                                    <PublishIcon
                                                                        fontSize="small"
                                                                        color={
                                                                            String(row.state || "").toUpperCase() === "PUBLISHED"
                                                                            || publishingId === row.id
                                                                                ? "disabled"
                                                                                : "primary"
                                                                        }
                                                                    />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: 2,
                                    mt: 2,
                                    flexWrap: "wrap",
                                }}
                            >
                                <FormControl size="small" sx={{ minWidth: 88 }}>
                                    <InputLabel id="grid-tpl-page-size-label">Rows</InputLabel>
                                    <Select
                                        labelId="grid-tpl-page-size-label"
                                        value={pageModel.pageSize}
                                        label="Rows"
                                        onChange={(e) => {
                                            const pageSize = Number(e.target.value);
                                            setPageModel({ page: 0, pageSize });
                                        }}
                                    >
                                        {[15, 25, 30].map((n) => (
                                            <MenuItem key={n} value={n}>{n}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Pagination
                                    color="primary"
                                    count={Math.max(1, Math.ceil((rowCount || 0) / pageModel.pageSize))}
                                    page={pageModel.page + 1}
                                    onChange={(_e, page) => setPageModel((prev) => ({ ...prev, page: page - 1 }))}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <DataGrid
                            rows={rows}
                            columns={getColumns(showDisabledColumn, handlePublishRow, publishingId, handleFreeChange, savingFreeId, handleRegenPreview, regeneratingId)}
                            rowCount={rowCount}
                            pageSizeOptions={[15, 25, 30]}
                            paginationMode="server"
                            sortingMode="server"
                            pagination={true}
                            paginationModel={pageModel}
                            onPaginationModelChange={handlePaginationModelChange}
                            sortModel={sort}
                            checkboxSelection
                            selectionModel={selectedRows}
                            experimentalFeatures={{ newEditingApi: false }}
                            onCellClick={handleCell}
                            onSortModelChange={handleSort}
                            onRowSelectionModelChange={handleSelectionModel}
                            columnVisibilityModel={{ id: false }}
                            hideFooterSelectedRowCount
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        id: false,
                                    },
                                },
                            }}
                        />
                    )}
                </Grid>
            </Grid>

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />

            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                callback={handleConfirmDelete}
                title="Confirm Delete"
                message={[
                    `Are you sure you want to delete ${selectedRows.length} selected template(s)? This action cannot be undone.`,
                    "Selected template(s) and its related Grid and its related Play will be deleted."
                ]}
                targetName={`${selectedRows.length} selected template(s)`}
            />

            <Dialog open={openGenWordNetworkDialog} onClose={handleGenWordNetworkClose} maxWidth="sm" fullWidth>
                <DialogTitle>Generate Word Network</DialogTitle>
                <DialogContent>
                    {genWordNetworkLoading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">Creating word network for null network puzzles…</Typography>
                        </Box>
                    ) : genWordNetworkResult ? (
                        genWordNetworkResult.error ? (
                            <Typography color="error">{genWordNetworkResult.error}</Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Typography><strong>Total found:</strong> {genWordNetworkResult.totalFound ?? "—"}</Typography>
                                <Typography><strong>Processed:</strong> {genWordNetworkResult.processed ?? "—"}</Typography>
                                {Array.isArray(genWordNetworkResult.titles) && genWordNetworkResult.titles.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Processed puzzles:</Typography>
                                        <Box sx={{ maxHeight: 200, overflow: "auto", p: 1, bgcolor: "grey.100", borderRadius: 1, fontSize: "0.85rem" }}>
                                            {genWordNetworkResult.titles.map((item, idx) => (
                                                <Typography key={idx} component="div" variant="body2" sx={{ wordBreak: "break-word", mb: 0.5 }}>
                                                    <strong>{item.title ?? "—"}</strong> ({item.tplId ?? ""})
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )
                    ) : (
                        <Typography>
                            Would you like to generate word networks for puzzles that don't have one yet?
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {genWordNetworkResult && !genWordNetworkLoading ? (
                        <Button onClick={handleGenWordNetworkClose} variant="contained">Close</Button>
                    ) : !genWordNetworkLoading ? (
                        <>
                            <Button onClick={handleGenWordNetworkClose}>Cancel</Button>
                            <Button onClick={handleGenWordNetworkConfirm} variant="contained" color="primary">OK</Button>
                        </>
                    ) : null}
                </DialogActions>
            </Dialog>

            <Dialog open={openGenPreImageDialog} onClose={handleGenPreImageClose} maxWidth="sm" fullWidth>
                <DialogTitle>Generate Preview Images</DialogTitle>
                <DialogContent>
                    {genPreImageLoading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">Generating preview images for puzzles…</Typography>
                        </Box>
                    ) : genPreImageResult ? (
                        genPreImageResult.error ? (
                            <Typography color="error">{genPreImageResult.error}</Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Typography><strong>Total found:</strong> {genPreImageResult.totalFound ?? "—"}</Typography>
                                <Typography><strong>Processed:</strong> {genPreImageResult.processed ?? "—"}</Typography>
                                {genPreImageResult.forceUpdateAll ? (
                                    <Typography variant="body2" color="text.secondary">Force update all was on — existing previews were regenerated.</Typography>
                                ) : null}
                                {Array.isArray(genPreImageResult.titles) && genPreImageResult.titles.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Processed puzzles:</Typography>
                                        <Box sx={{ maxHeight: 200, overflow: "auto", p: 1, bgcolor: "grey.100", borderRadius: 1, fontSize: "0.85rem" }}>
                                            {genPreImageResult.titles.map((item, idx) => (
                                                <Typography key={idx} component="div" variant="body2" sx={{ wordBreak: "break-word", mb: 0.5 }}>
                                                    <strong>{item.title ?? "—"}</strong> ({item.tplId ?? ""})
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
                            <Typography>
                                Generate preview images for puzzles that don&apos;t have one yet, or force-regenerate all so older previews pick up the story sidebar.
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={genPreImageForce}
                                        onChange={(e) => setGenPreImageForce(e.target.checked)}
                                    />
                                }
                                label="Force update all"
                            />
                            {genPreImageForce ? (
                                <Alert severity="warning">
                                    Regenerates preview images for every READY_TO_REVIEW / REVIEWED / PUBLISHED puzzle, including ones that already have a preview.
                                </Alert>
                            ) : null}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {genPreImageResult && !genPreImageLoading ? (
                        <Button onClick={handleGenPreImageClose} variant="contained">Close</Button>
                    ) : !genPreImageLoading ? (
                        <>
                            <Button onClick={handleGenPreImageClose}>Cancel</Button>
                            <Button onClick={handleGenPreImageConfirm} variant="contained" color="primary">OK</Button>
                        </>
                    ) : null}
                </DialogActions>
            </Dialog>

            <Dialog open={openDuplicateScatteredDialog} onClose={duplicateScatteredLoading ? undefined : handleDuplicateScatteredClose} maxWidth="sm" fullWidth>
                <DialogTitle>Published hidden message — duplicate scattered</DialogTitle>
                <DialogContent>
                    {duplicateScatteredLoading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">Checking published hidden-message templates…</Typography>
                        </Box>
                    ) : duplicateScatteredResult?.error ? (
                        <Typography color="error">{duplicateScatteredResult.error}</Typography>
                    ) : duplicateScatteredResult ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Typography><strong>Published hidden message count:</strong> {duplicateScatteredResult.publishedHiddenMessageCount ?? "—"}</Typography>
                            <Typography><strong>Checked (with filled coordinates):</strong> {duplicateScatteredResult.checkedWithFilledCoordinates ?? "—"}</Typography>
                            <Typography color={duplicateScatteredResult.violationCount > 0 ? "error.main" : "text.primary"}>
                                <strong>Violation count:</strong> {duplicateScatteredResult.violationCount ?? "—"}
                            </Typography>
                            {Array.isArray(duplicateScatteredResult.puzzles) && duplicateScatteredResult.puzzles.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Violations</Typography>
                                    <Box sx={{ maxHeight: 280, overflow: "auto", p: 1, bgcolor: "grey.100", borderRadius: 1, fontSize: "0.85rem" }}>
                                        {duplicateScatteredResult.puzzles.map((p, idx) => (
                                            <Typography key={p.id || idx} component="div" variant="body2" sx={{ wordBreak: "break-word", mb: 0.75 }}>
                                                <strong>{p.name ?? p.title ?? "—"}</strong>
                                                {p.id != null && (
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                        {p.id}
                                                    </Typography>
                                                )}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDuplicateScatteredClose} variant="contained" disabled={duplicateScatteredLoading}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openGenPdfDialog} onClose={handleGenPdfClose} maxWidth="sm" fullWidth>
                <DialogTitle>Generate PDFs</DialogTitle>
                <DialogContent>
                    {genPdfLoading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">
                                Starting background job…
                            </Typography>
                        </Box>
                    ) : genPdfResult ? (
                        genPdfResult.error ? (
                            <Typography color="error">{genPdfResult.error}</Typography>
                        ) : genPdfResult.async ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {genPdfResult.alreadyRunning ? (
                                    <Alert severity="warning">{genPdfResult.message ?? "A replace-all PDF job is already running."}</Alert>
                                ) : (
                                    <Alert severity="success">{genPdfResult.message ?? "Replace-all PDF job started."}</Alert>
                                )}
                                <Typography><strong>Total:</strong> {genPdfResult.total ?? "—"}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Progress is printed in puzzle-be logs as current/total (look for [GEN-PDF]). Newest published puzzles are processed first.
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Typography><strong>Total found:</strong> {genPdfResult.totalFound ?? "—"}</Typography>
                                <Typography><strong>Processed:</strong> {genPdfResult.processed ?? "—"}</Typography>
                                {Array.isArray(genPdfResult.titles) && genPdfResult.titles.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Processed puzzles:</Typography>
                                        <Box sx={{ maxHeight: 200, overflow: "auto", p: 1, bgcolor: "grey.100", borderRadius: 1, fontSize: "0.85rem" }}>
                                            {genPdfResult.titles.map((item, idx) => (
                                                <Typography key={idx} component="div" variant="body2" sx={{ wordBreak: "break-word", mb: 0.5 }}>
                                                    <strong>{item.title ?? "—"}</strong> ({item.tplId ?? ""})
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
                            <Typography>
                                Generate missing PDFs, or replace all existing files so they use the current company logo.
                            </Typography>
                            <FormControl fullWidth size="small">
                                <InputLabel id="gen-pdf-scope-label">Which PDFs</InputLabel>
                                <Select
                                    labelId="gen-pdf-scope-label"
                                    value={genPdfMode}
                                    label="Which PDFs"
                                    onChange={(e) => setGenPdfMode(e.target.value)}
                                >
                                    <MenuItem value="missing">Missing PDFs</MenuItem>
                                    <MenuItem value="all-existing">All existing PDFs</MenuItem>
                                </Select>
                            </FormControl>
                            <Alert severity="info">
                                Runs in the background, newest published first. Restart puzzle-be after replacing logo192.png so a logo change is used. Watch server logs for current/total.
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {genPdfResult && !genPdfLoading ? (
                        <Button onClick={handleGenPdfClose} variant="contained">Close</Button>
                    ) : !genPdfLoading ? (
                        <>
                            <Button onClick={handleGenPdfClose}>Cancel</Button>
                            <Button onClick={handleGenPdfConfirm} variant="contained" color="primary">OK</Button>
                        </>
                    ) : null}
                </DialogActions>
            </Dialog>

            <Dialog open={openGenBubblesDialog} onClose={handleGenBubblesClose} maxWidth="sm" fullWidth>
                <DialogTitle>Generate clustered bubbles</DialogTitle>
                <DialogContent>
                    {!genBubblesResult && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            All English/French
                        </Alert>
                    )}
                    {genBubblesLoading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">Generating clustered bubbles…</Typography>
                        </Box>
                    ) : genBubblesResult ? (
                        genBubblesResult.error ? (
                            <Typography color="error">{genBubblesResult.error}</Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Typography><strong>Updated:</strong> {genBubblesResult.updated ?? "—"}</Typography>
                                <Typography><strong>Total published:</strong> {genBubblesResult.totalPublished ?? "—"}</Typography>
                            </Box>
                        )
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Run clustered bubble generation for published grid templates. Page is the batch offset (0-based); batch size is how many templates to process in this request.
                            </Typography>
                            <TextField
                                label="Page"
                                type="number"
                                size="small"
                                fullWidth
                                inputProps={{ min: 0, step: 1 }}
                                value={genBubblesPage}
                                onChange={(e) => {
                                    const n = parseInt(e.target.value, 10);
                                    setGenBubblesPage(Number.isFinite(n) && n >= 0 ? n : 0);
                                }}
                            />
                            <TextField
                                label="Batch size"
                                type="number"
                                size="small"
                                fullWidth
                                inputProps={{ min: 1, step: 1 }}
                                value={genBubblesBatchSize}
                                onChange={(e) => {
                                    const n = parseInt(e.target.value, 10);
                                    setGenBubblesBatchSize(Number.isFinite(n) && n >= 1 ? n : 1);
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={genBubblesForce}
                                        onChange={(e) => setGenBubblesForce(e.target.checked)}
                                    />
                                }
                                label="Force"
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {genBubblesResult && !genBubblesLoading ? (
                        <Button onClick={handleGenBubblesClose} variant="contained">Close</Button>
                    ) : !genBubblesLoading ? (
                        <>
                            <Button onClick={handleGenBubblesClose}>Cancel</Button>
                            <Button onClick={handleGenBubblesConfirm} variant="contained" color="primary">Run</Button>
                        </>
                    ) : null}
                </DialogActions>
            </Dialog>

            <Dialog open={openRePublishDialog} onClose={handleRePublishClose} maxWidth="sm" fullWidth>
                <DialogTitle>Re-Publish</DialogTitle>
                <DialogContent>
                    {rePublishLoading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">Re-publishing published grid templates…</Typography>
                        </Box>
                    ) : rePublishResult ? (
                        rePublishResult.error ? (
                            <Typography color="error">{rePublishResult.error}</Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Typography><strong>Zone:</strong> {rePublishResult.zone ?? "—"}</Typography>
                                <Typography><strong>Range start:</strong> {rePublishResult.rangeStart ?? "—"}</Typography>
                                <Typography><strong>Range end:</strong> {rePublishResult.rangeEnd ?? "—"}</Typography>
                                <Typography><strong>Total in range:</strong> {rePublishResult.totalInRange ?? "—"}</Typography>
                                <Typography><strong>Updated:</strong> {rePublishResult.updated ?? "—"}</Typography>
                            </Box>
                        )
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                            <Alert severity="warning">
                                The request sends <strong>since</strong> as a query parameter (the date below, default six months ago). The server
                                includes only published puzzles that match its rules (for example, in the 2026 window) and your <strong>since</strong>{" "}
                                filter. For each included puzzle, the <strong>published date</strong> is set to a new value chosen{" "}
                                <strong>at random</strong> in the <strong>next 6 months</strong>, replacing the previous date.
                            </Alert>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Since"
                                    value={rePublishSince}
                                    onChange={(v) => {
                                        setRePublishSince(v);
                                        setRePublishDateError("");
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            error: Boolean(rePublishDateError),
                                            helperText:
                                                rePublishDateError ||
                                                "Sent as ?since=YYYY-MM-DD. Defaults to six months before today; change it if you need a different lower bound.",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <Typography variant="body2" color="text.secondary">
                                Click OK only if you intend to run this job now.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {rePublishResult && !rePublishLoading ? (
                        <Button onClick={handleRePublishClose} variant="contained">Close</Button>
                    ) : !rePublishLoading ? (
                        <>
                            <Button onClick={handleRePublishClose}>Cancel</Button>
                            <Button onClick={handleRePublishConfirm} variant="contained" color="primary">OK</Button>
                        </>
                    ) : null}
                </DialogActions>
            </Dialog>

            <CustomFabs tabIndex={0} callback={handleFabClick} />
        </Box>
    );
}

